import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronLeft, ChevronRight, Volume2, VolumeX, Hand, Camera, CameraOff, RefreshCw, AlertTriangle, Mic } from 'lucide-react';
import MissSpark from '@/components/MissSpark';
import VoiceChat from '@/components/VoiceChat';
import { useAudio } from '@/context/AudioContext';
import { toast } from 'sonner';
import useCamera from '@/hooks/useCamera';
import useAttentionTracker from '@/hooks/useAttentionTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const LETTER_COLORS = [
  'from-spark to-pink-400',
  'from-coral to-orange-400',
  'from-sunshine to-yellow-400',
  'from-seagreen to-teal',
  'from-teal to-aqua',
  'from-purple-400 to-pink-400',
];

const ABCDModule = () => {
  const navigate = useNavigate();
  const { playSound } = useAudio();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [letterData, setLetterData] = useState(null);
  const [sparkMood, setSparkMood] = useState('happy');
  const [sparkMessage, setSparkMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [attentionWarning, setAttentionWarning] = useState(null);
  const [isListening, setIsListening] = useState(false);
  
  const audioRef = useRef(null);
  const sessionId = useRef(`abcd_${Date.now()}`);

  const currentLetter = LETTERS[currentIndex];
  const colorClass = LETTER_COLORS[currentIndex % LETTER_COLORS.length];

  // Camera hook
  const {
    cameras,
    selectedCamera,
    cameraOn,
    videoRef,
    refreshCameras,
    toggleCamera,
    changeCamera
  } = useCamera();

  // Attention tracking hook
  const {
    isDistracted,
    distractionType,
    attentionScore,
    faceDetected,
    resetDistraction
  } = useAttentionTracker(cameraOn, videoRef.current);

  // Play TTS audio with Web Speech fallback
  const speakText = useCallback(async (text, emotion = 'excited') => {
    if (!voiceEnabled || !text) return;
    
    try {
      setIsSpeaking(true);
      
      const response = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text,
          tutor: 'spark',
          emotion: emotion
        })
      });
      
      const data = await response.json();
      
      if (data.audio_url && !data.mock) {
        if (audioRef.current) {
          audioRef.current.src = data.audio_url;
          await audioRef.current.play();
        }
      } else {
        // Fallback to Web Speech Synthesis (Miss Spark - cheerful female)
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'hi-IN';
          utterance.rate = 1.1; // Slightly faster, energetic
          utterance.pitch = 1.2; // Higher pitch, cheerful
          utterance.onend = () => setIsSpeaking(false);
          utterance.onerror = () => setIsSpeaking(false);
          window.speechSynthesis.speak(utterance);
          return;
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      // Fallback to Web Speech on error
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'hi-IN';
        utterance.rate = 1.1;
        utterance.pitch = 1.2;
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
        return;
      }
    } finally {
      setIsSpeaking(false);
    }
  }, [voiceEnabled]);

  // Handle voice chat response
  const handleVoiceResponse = useCallback((data) => {
    if (data.response) {
      setSparkMessage(data.response);
      setSparkMood(data.emotion === 'excited' ? 'singing' : data.emotion === 'encouraging' ? 'encouraging' : 'happy');
    }
  }, []);

  // Handle distraction detection
  useEffect(() => {
    if (isDistracted && distractionType && !attentionWarning) {
      handleDistraction(distractionType);
    }
  }, [isDistracted, distractionType]);

  const handleDistraction = async (type) => {
    try {
      const response = await fetch(`${API}/interaction/attention?distraction_type=${type}&session_id=${sessionId.current}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      // Use gentle messages for toddlers
      const gentleMessages = {
        looking_away: [
          "Beta, idhar dekho! Miss Spark ko dekho! 👀",
          "Hey sweetie, mujhe dekho! ABCD seekhte hain!",
          "Arre baby, focus! Mere saath gaao!"
        ],
        yawning: [
          "Neend aa gayi? Ek jump maaro aur wapas aao!",
          "Tired ho gaye? Thoda rest karo phir seekhenge!",
          "Ubaasi? Chalo taali bajao aur energetic ho jao!"
        ],
        drowsy: [
          "Aankhein kholo baby! ABC time hai!",
          "Uthoooo! Miss Spark ke saath gaana gaao!",
          "Wake up sweetie! Let's sing together!"
        ]
      };

      const messages = gentleMessages[type] || gentleMessages.looking_away;
      const message = messages[Math.floor(Math.random() * messages.length)];
      
      setAttentionWarning(message);
      setSparkMood('encouraging');
      playSound('wrong');
      speakText(message, 'encouraging');
      
      setTimeout(() => {
        setAttentionWarning(null);
        setSparkMood('happy');
        resetDistraction();
      }, 4000);
    } catch (error) {
      console.error('Attention API error:', error);
    }
  };

  // Fetch letter content
  const fetchLetterContent = useCallback(async (letter) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API}/learn/abcd/${letter}`);
      const data = await response.json();
      setLetterData(data);
      setSparkMessage(data.song);
      setSparkMood('singing');
      
      await speakText(data.song, 'excited');
      
      const chatResponse = await fetch(`${API}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: `Teach letter ${letter}`,
          session_id: sessionId.current,
          tutor: 'spark',
          module: 'abcd',
          context: { letter }
        })
      });
      
      const chatData = await chatResponse.json();
      
      setTimeout(async () => {
        setSparkMessage(chatData.response);
        setSparkMood(chatData.emotion || 'happy');
        await speakText(chatData.response, chatData.emotion || 'happy');
      }, 3000);
      
    } catch (error) {
      console.error('Error fetching letter:', error);
    } finally {
      setIsLoading(false);
    }
  }, [speakText]);

  useEffect(() => {
    fetchLetterContent(currentLetter);
  }, [currentLetter, fetchLetterContent]);

  const goToNext = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    if (currentIndex < LETTERS.length - 1) {
      setCurrentIndex(prev => prev + 1);
      playSound('success');
    } else {
      setShowCelebration(true);
      speakText("Yaaay! You finished all the letters! You are a superstar!", 'excited');
      setTimeout(() => setShowCelebration(false), 3000);
    }
  };

  const goToPrevious = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleClap = async () => {
    playSound('success');
    setSparkMood('excited');
    const celebrationMsg = "Yaaay! You clapped! Such a good job! Let's learn the next letter!";
    setSparkMessage(celebrationMsg);
    toast.success("Great clapping! 👏", { duration: 2000 });
    
    await speakText(celebrationMsg, 'excited');
    
    setTimeout(() => {
      goToNext();
    }, 2500);
  };

  const handleReplay = () => {
    if (sparkMessage) {
      speakText(sparkMessage, sparkMood);
    }
  };

  return (
    <div className="min-h-screen relative z-10 pb-8">
      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setIsSpeaking(false)}
        onError={() => setIsSpeaking(false)}
      />
      
      {/* Attention Warning Banner */}
      <AnimatePresence>
        {attentionWarning && (
          <motion.div
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            exit={{ y: -100 }}
            className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-spark to-pink-400 text-white py-4 px-6 text-center shadow-lg"
          >
            <div className="flex items-center justify-center gap-3">
              <AlertTriangle className="w-6 h-6 animate-bounce" />
              <span className="font-heading text-lg">{attentionWarning}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Back Button */}
      <Button
        data-testid="back-btn"
        variant="ghost"
        onClick={() => navigate('/')}
        className="back-button bg-white/80 backdrop-blur-sm"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Home
      </Button>

      {/* Voice Toggle */}
      <Button
        data-testid="voice-toggle-btn"
        variant="ghost"
        onClick={() => setVoiceEnabled(!voiceEnabled)}
        className="fixed top-4 right-4 z-50 bg-white/80 backdrop-blur-sm"
      >
        {voiceEnabled ? (
          <Volume2 className="w-5 h-5 text-seagreen" />
        ) : (
          <VolumeX className="w-5 h-5 text-muted-foreground" />
        )}
      </Button>

      {/* Header */}
      <div className="pt-20 pb-8 px-6 text-center">
        <h1 className="text-3xl font-heading text-foreground mb-2">Learn ABCD with Miss Spark!</h1>
        <div className="flex justify-center gap-2">
          {LETTERS.slice(0, 10).map((letter, idx) => (
            <div
              key={letter}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'bg-spark scale-125' 
                  : idx < currentIndex 
                    ? 'bg-seagreen' 
                    : 'bg-muted'
              }`}
            />
          ))}
          {currentIndex >= 10 && <span className="text-muted-foreground">...</span>}
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Letter Display */}
          <motion.div
            key={currentLetter}
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={`learning-card bg-gradient-to-br ${colorClass} flex flex-col items-center justify-center`}
          >
            <span className="letter-display text-white drop-shadow-lg">
              {currentLetter}
            </span>
            {letterData && (
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-white text-2xl font-heading mt-4"
              >
                {currentLetter} for {letterData.word}
              </motion.p>
            )}
          </motion.div>

          {/* Miss Spark + Camera */}
          <div className="flex flex-col items-center">
            <div className={`tutor-container spark p-6 rounded-3xl mb-4 ${isSpeaking ? 'animate-pulse-soft' : ''} ${attentionWarning ? 'animate-pulse' : ''}`}>
              <MissSpark mood={sparkMood} size="large" speaking={isSpeaking} />
            </div>
            
            {/* Speech Bubble */}
            <AnimatePresence mode="wait">
              <motion.div
                key={sparkMessage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="speech-container text-center"
              >
                <p className="text-foreground font-medium">{sparkMessage || "Let's learn together!"}</p>
                
                {sparkMessage && (
                  <Button
                    data-testid="replay-btn"
                    variant="ghost"
                    size="sm"
                    onClick={handleReplay}
                    disabled={isSpeaking}
                    className="mt-2 text-spark hover:text-spark/80"
                  >
                    <Volume2 className="w-4 h-4 mr-1" />
                    {isSpeaking ? 'Speaking...' : 'Replay'}
                  </Button>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Action Button */}
            <motion.div 
              className="mt-6"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                data-testid="clap-btn"
                onClick={handleClap}
                disabled={isSpeaking}
                className="btn-playful btn-spark text-xl"
              >
                <Hand className="w-6 h-6 mr-2" />
                Clap Your Hands! 👏
              </Button>
            </motion.div>
          </div>

          {/* Webcam Section */}
          <div className="flex flex-col items-center">
            <div className={`webcam-frame w-full aspect-video bg-chalkboard ${attentionWarning ? 'ring-4 ring-spark' : ''}`}>
              {cameraOn ? (
                <>
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover transform scale-x-[-1]"
                    autoPlay
                    playsInline
                    muted
                  />
                  {/* Face Detection Indicator */}
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${faceDetected ? 'bg-seagreen text-white' : 'bg-muted text-muted-foreground'}`}>
                    {faceDetected ? '😊 Face OK' : '🔍 No Face'}
                  </div>
                  {/* Attention Score */}
                  {faceDetected && (
                    <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium bg-white/80">
                      Attention: {Math.round(attentionScore * 100)}%
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white">
                    <CameraOff className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Camera Off</p>
                    <p className="text-xs opacity-70">Turn on to track attention</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Camera Selector */}
            {cameraOn && cameras.length > 1 && (
              <div className="mt-2 w-full">
                <select
                  data-testid="camera-select"
                  value={selectedCamera}
                  onChange={(e) => changeCamera(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-spark"
                >
                  {cameras.map((cam, idx) => (
                    <option key={cam.deviceId} value={cam.deviceId}>
                      {cam.label || `Camera ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Camera Controls */}
            <div className="flex gap-2 mt-3 w-full">
              <Button
                data-testid="camera-toggle-btn"
                variant={cameraOn ? "default" : "outline"}
                size="sm"
                onClick={toggleCamera}
                className={`flex-1 ${cameraOn ? 'bg-seagreen hover:bg-seagreen-dark' : ''}`}
              >
                {cameraOn ? <Camera className="w-4 h-4 mr-2" /> : <CameraOff className="w-4 h-4 mr-2" />}
                {cameraOn ? "Camera On" : "Turn On Camera"}
              </Button>
              
              {cameraOn && (
                <Button
                  data-testid="refresh-cameras-btn"
                  variant="outline"
                  size="sm"
                  onClick={refreshCameras}
                  title="Refresh camera list"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Voice Chat Section */}
            <div className="mt-4 p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-card w-full">
              <h3 className="text-sm font-heading text-foreground mb-3 flex items-center gap-2">
                <Mic className="w-4 h-4 text-spark" />
                Voice Chat with Miss Spark
              </h3>
              <VoiceChat
                tutor="spark"
                module="abcd"
                context={{ letter: currentLetter }}
                sessionId={sessionId.current}
                onResponse={handleVoiceResponse}
                onListeningChange={setIsListening}
                onSpeakingChange={setIsSpeaking}
              />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-6 mt-8">
          <Button
            data-testid="prev-btn"
            variant="outline"
            size="lg"
            onClick={goToPrevious}
            disabled={currentIndex === 0 || isSpeaking}
            className="rounded-full w-14 h-14"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          
          <span className="text-2xl font-heading text-foreground">
            {currentIndex + 1} / {LETTERS.length}
          </span>
          
          <Button
            data-testid="next-btn"
            size="lg"
            onClick={goToNext}
            disabled={isSpeaking}
            className="btn-playful btn-spark rounded-full w-14 h-14"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Celebration */}
      {showCelebration && (
        <div className="celebration">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                backgroundColor: ['#FF6B9D', '#FFE66D', '#7FDBDA', '#2E8B57'][i % 4],
                animationDelay: `${Math.random() * 0.5}s`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ABCDModule;
